/**
 * Companion Data Adapter
 * 
 * This module handles all data transformations between the API response format
 * and the frontend data structures. When the database schema changes, only
 * this file needs to be updated.
 */

import { ChatbotPersonality, MediaAsset } from '../types/chatbot';

// Raw API Response Types (matches current API structure)
export interface ApiCompanionName {
  first_name: string;
  last_name: string | null;
  nick_name: string | null;
}

export interface ApiCompanionAsset {
  asset_id: string;
  presigned_url: string;
  description: string;
  type: 'photo' | 'video';
}

export interface ApiCompanionAssets {
  primary_photo: ApiCompanionAsset | null;
  primary_video: ApiCompanionAsset | null;
  gallery: ApiCompanionAsset[];
  outfits: object;
}

export interface ApiCompanionAttributes {
  physical: {
    ethnicity: string | null;
    eye_color: string | null;
    hair_style: string | null;
    hair_color: string | null;
    body_type: string | null;
  };
  personal: {
    occupation: string | null;
    hobby: string[];
  };
}

export interface ApiCompanionTraits {
  positive: string[];
  negative: string[];
}

export interface ApiCompanionPersonality {
  positive: string[];
  negative: string[];
}

export interface ApiCompanionSocial {
  posts: any[];
}

// Raw API Companion Response
export interface ApiCompanion {
  companion_id: string;
  name: ApiCompanionName;
  description: string | null;
  created_by_user: string | null;
  is_public: boolean;
  age: number | null;
  about: string | null;
  birthday: string | null;
  attributes: ApiCompanionAttributes;
  traits: ApiCompanionTraits;
  personality: ApiCompanionPersonality;
  categories: string[];
  assets: ApiCompanionAssets;
  social: ApiCompanionSocial;
}

export interface ApiCompanionListResponse {
  companions: ApiCompanion[];
}

// Frontend Data Structures (normalized for UI consumption)
export interface NormalizedCompanion {
  id: string;
  displayName: string;
  firstName: string;
  lastName: string | null;
  nickName: string | null;
  description: string;
  age: number;
  isPublic: boolean;
  about: string | null;
  birthday: string | null;
  categories: string[];
  
  // Assets (normalized)
  primaryPhoto: string | null;
  primaryVideo: string | null;
  posterUrl: string | null;
  galleryAssets: MediaAsset[];
  
  // Attributes (normalized)
  physicalAttributes: {
    ethnicity?: string;
    eyeColor?: string;
    hairStyle?: string;
    hairColor?: string;
    bodyType?: string;
  };
  personalAttributes: {
    occupation?: string;
    hobbies: string[];
  };
  
  // Personality (normalized)
  positiveTraits: string[];
  negativeTraits: string[];
  personalityTraits: string[];
  
  // Metadata
  createdBy: string | null;
  socialPosts: any[];
}

/**
 * Companion Data Adapter Class
 * Handles all transformations between API and frontend formats
 */
export class CompanionAdapter {
  
  /**
   * Extract display name from API name structure
   */
  static extractDisplayName(apiName: ApiCompanionName | string): string {
    if (typeof apiName === 'string') {
      return apiName || 'Unknown';
    }
    
    if (apiName.nick_name) {
      return apiName.nick_name;
    }
    
    const fullName = `${apiName.first_name || ''} ${apiName.last_name || ''}`.trim();
    return fullName || apiName.first_name || 'Unknown';
  }

  /**
   * Extract individual name components
   */
  static extractNameComponents(apiName: ApiCompanionName | string) {
    if (typeof apiName === 'string') {
      return {
        firstName: apiName || 'Unknown',
        lastName: null,
        nickName: null
      };
    }
    
    return {
      firstName: apiName.first_name || 'Unknown',
      lastName: apiName.last_name,
      nickName: apiName.nick_name
    };
  }

  /**
   * Process assets and extract URLs
   */
  static processAssets(apiAssets: ApiCompanionAssets): {
    primaryPhoto: string | null;
    primaryVideo: string | null;
    posterUrl: string | null;
    galleryAssets: MediaAsset[];
  } {
    const primaryPhoto = apiAssets.primary_photo?.presigned_url || null;
    const primaryVideo = apiAssets.primary_video?.presigned_url || null;
    
    // Determine poster URL for videos
    let posterUrl = null;
    if (primaryVideo) {
      posterUrl = primaryPhoto || 
        apiAssets.gallery.find(asset => asset.type === 'photo')?.presigned_url || 
        null;
    }

    // Build gallery assets
    const galleryAssets: MediaAsset[] = [];
    
    // Add primary assets to gallery
    if (primaryPhoto) {
      galleryAssets.push({
        id: 'primary_photo',
        url: primaryPhoto,
        type: 'image'
      });
    }
    
    if (primaryVideo) {
      galleryAssets.push({
        id: 'primary_video',
        url: primaryVideo,
        type: 'video'
      });
    }
    
    // Add gallery assets
    apiAssets.gallery.forEach((asset, index) => {
      galleryAssets.push({
        id: asset.asset_id || `gallery_${index}`,
        url: asset.presigned_url,
        type: asset.type === 'photo' ? 'image' : 'video'
      });
    });

    return {
      primaryPhoto,
      primaryVideo,
      posterUrl,
      galleryAssets
    };
  }

  /**
   * Process attributes into normalized format
   */
  static processAttributes(apiAttributes: ApiCompanionAttributes) {
    return {
      physicalAttributes: {
        ethnicity: apiAttributes.physical.ethnicity || undefined,
        eyeColor: apiAttributes.physical.eye_color || undefined,
        hairStyle: apiAttributes.physical.hair_style || undefined,
        hairColor: apiAttributes.physical.hair_color || undefined,
        bodyType: apiAttributes.physical.body_type || undefined,
      },
      personalAttributes: {
        occupation: apiAttributes.personal.occupation || undefined,
        hobbies: Array.isArray(apiAttributes.personal.hobby) ? apiAttributes.personal.hobby : []
      }
    };
  }

  /**
   * Convert API companion to normalized companion
   */
  static apiToNormalized(apiCompanion: ApiCompanion): NormalizedCompanion {
    const nameComponents = this.extractNameComponents(apiCompanion.name);
    const displayName = this.extractDisplayName(apiCompanion.name);
    const assets = this.processAssets(apiCompanion.assets);
    const attributes = this.processAttributes(apiCompanion.attributes);

    return {
      id: apiCompanion.companion_id,
      displayName,
      firstName: nameComponents.firstName,
      lastName: nameComponents.lastName,
      nickName: nameComponents.nickName,
      description: apiCompanion.description || '',
      age: apiCompanion.age || 20,
      isPublic: apiCompanion.is_public,
      about: apiCompanion.about,
      birthday: apiCompanion.birthday,
      categories: apiCompanion.categories || [],
      
      // Assets
      primaryPhoto: assets.primaryPhoto,
      primaryVideo: assets.primaryVideo,
      posterUrl: assets.posterUrl,
      galleryAssets: assets.galleryAssets,
      
      // Attributes
      physicalAttributes: attributes.physicalAttributes,
      personalAttributes: attributes.personalAttributes,
      
      // Personality
      positiveTraits: apiCompanion.traits?.positive || [],
      negativeTraits: apiCompanion.traits?.negative || [],
      personalityTraits: apiCompanion.personality?.positive || [],
      
      // Metadata
      createdBy: apiCompanion.created_by_user,
      socialPosts: apiCompanion.social?.posts || []
    };
  }

  /**
   * Convert normalized companion to ChatbotPersonality
   */
  static normalizedToChatbot(companion: NormalizedCompanion): ChatbotPersonality {
    // Build personality description
    const personalityParts = [];
    
    if (companion.physicalAttributes.ethnicity) personalityParts.push(companion.physicalAttributes.ethnicity);
    if (companion.physicalAttributes.eyeColor) personalityParts.push(`${companion.physicalAttributes.eyeColor} eyes`);
    if (companion.physicalAttributes.hairColor && companion.physicalAttributes.hairStyle) {
      personalityParts.push(`${companion.physicalAttributes.hairColor} ${companion.physicalAttributes.hairStyle} hair`);
    } else if (companion.physicalAttributes.hairColor) {
      personalityParts.push(`${companion.physicalAttributes.hairColor} hair`);
    } else if (companion.physicalAttributes.hairStyle) {
      personalityParts.push(`${companion.physicalAttributes.hairStyle} hair`);
    }
    if (companion.physicalAttributes.bodyType) personalityParts.push(companion.physicalAttributes.bodyType);
    if (companion.personalAttributes.occupation) personalityParts.push(companion.personalAttributes.occupation);
    if (Array.isArray(companion.personalAttributes.hobbies) && companion.personalAttributes.hobbies.length > 0) {
      personalityParts.push(`enjoys ${companion.personalAttributes.hobbies.join(', ')}`);
    }

    const personalityDescription = personalityParts.join(', ');
    
    // Combine all personality traits
    const allTraits = [
      ...companion.positiveTraits,
      ...companion.personalityTraits
    ];
    const personalityString = allTraits.join(', ');

    return {
      id: companion.id,
      name: companion.displayName,
      age: companion.age,
      personality: personalityString || personalityDescription || '',
      description: companion.description,
      welcomeMessage: `Hello! I'm ${companion.displayName}. Nice to meet you!`,
      systemPrompt: `You are ${companion.displayName}, ${companion.description || 'a friendly AI companion'}. ${personalityDescription ? `You have ${personalityDescription}.` : ''}`,
      avatar: companion.primaryPhoto || companion.primaryVideo || '',
      avatarType: companion.primaryVideo ? 'video' : 'image',
      posterUrl: companion.posterUrl || '',
      gallery: companion.galleryAssets.filter(asset => asset.type === 'image').map(asset => asset.url),
      galleryAssets: companion.galleryAssets,
      mood: 'caring' as any,
      specialties: companion.categories,
      attributes: {
        physical: {
          ethnicity: companion.physicalAttributes.ethnicity,
          eye_color: companion.physicalAttributes.eyeColor,
          hair_style: companion.physicalAttributes.hairStyle,
          hair_color: companion.physicalAttributes.hairColor,
          body_type: companion.physicalAttributes.bodyType,
        },
        personal: {
          occupation: companion.personalAttributes.occupation,
          hobby: Array.isArray(companion.personalAttributes.hobbies) ? companion.personalAttributes.hobbies.join(', ') || undefined : undefined,
        }
      }
    };
  }

  /**
   * Convert API companion directly to ChatbotPersonality (convenience method)
   */
  static apiToChatbot(apiCompanion: ApiCompanion): ChatbotPersonality {
    const normalized = this.apiToNormalized(apiCompanion);
    return this.normalizedToChatbot(normalized);
  }

  /**
   * Convert API response to normalized companions list
   */
  static apiResponseToNormalized(apiResponse: ApiCompanionListResponse): NormalizedCompanion[] {
    return apiResponse.companions.map(companion => this.apiToNormalized(companion));
  }

  /**
   * Convert API response to chatbot personalities list
   */
  static apiResponseToChatbots(apiResponse: ApiCompanionListResponse): ChatbotPersonality[] {
    return apiResponse.companions.map(companion => this.apiToChatbot(companion));
  }

  /**
   * Legacy compatibility: convert old companion format to normalized
   * This helps with backward compatibility during transitions
   */
  static legacyToNormalized(legacyCompanion: any): NormalizedCompanion {
    // Handle legacy companion format if needed
    if (legacyCompanion.companion_id) {
      // Already in new format, use regular conversion
      return this.apiToNormalized(legacyCompanion);
    }

    // Handle old format
    return {
      id: legacyCompanion.id || legacyCompanion.name || 'unknown',
      displayName: legacyCompanion.name || 'Unknown',
      firstName: legacyCompanion.name || 'Unknown',
      lastName: null,
      nickName: null,
      description: legacyCompanion.description || '',
      age: legacyCompanion.age || 20,
      isPublic: legacyCompanion.is_public ?? true,
      about: null,
      birthday: null,
      categories: legacyCompanion.categories || legacyCompanion.specialties || [],
      
      primaryPhoto: legacyCompanion.avatar_url || legacyCompanion.avatar || null,
      primaryVideo: null,
      posterUrl: null,
      galleryAssets: legacyCompanion.gallery?.map((url: string, index: number) => ({
        id: `legacy_${index}`,
        url,
        type: 'image' as const
      })) || [],
      
      physicalAttributes: {},
      personalAttributes: { hobbies: [] },
      
      positiveTraits: [],
      negativeTraits: [],
      personalityTraits: [],
      
      createdBy: null,
      socialPosts: []
    };
  }
}